import { CalendarDays, FileText, Kanban } from 'lucide-react';
import type { PathResolver } from '@/components/layout/workspace/types';
import type { Attachment } from '@/lib/attachments';
import type { BoardItem, BoardColumn } from '@/components/ui/KanbanBoard';
import type { TimeShift } from '@/lib/timeGrid';

export const HOME = '/design/operations';
export const SAMPLE_NOW = Date.UTC(2026, 9, 23, 10);
export const COLUMNS: BoardColumn[] = [
  { id: 'received', title: 'Пришёл', tone: 'info' },
  { id: 'payment', title: 'На оплату', tone: 'warn' },
  { id: 'paid', title: 'Оплачен', tone: 'success' },
];
export interface SampleBoardItem extends BoardItem {
  minorUnits: number;
  currency: string;
  dueAt: string;
}
export const CARDS: SampleBoardItem[] = [
  {
    id: '042',
    title: 'Обслуживание инфраструктуры',
    columnId: 'received',
    minorUnits: 4820000,
    currency: 'RUB',
    dueAt: '2026-10-26T15:00:00Z',
    attachments: 2,
  },
  {
    id: '043',
    title: 'Продление лицензии',
    columnId: 'received',
    minorUnits: 125000,
    currency: 'USD',
    dueAt: '2026-10-24T15:00:00Z',
    attachments: 1,
  },
  {
    id: '044',
    title: 'Аренда оборудования',
    columnId: 'payment',
    minorUnits: 19650000,
    currency: 'RUB',
    dueAt: '2026-10-22T15:00:00Z',
    attachments: 1,
  },
  {
    id: '045',
    title: 'Доставка документов',
    columnId: 'payment',
    minorUnits: 240000,
    currency: 'RUB',
    dueAt: '2026-10-23T15:00:00Z',
  },
  {
    id: '046',
    title: 'Поддержка сервиса',
    columnId: 'paid',
    minorUnits: 7500000,
    currency: 'RUB',
    dueAt: '2026-10-28T15:00:00Z',
    attachments: 1,
  },
];
export interface SampleAttachment extends Attachment {
  area: 'card' | 'comment';
}
export const FILES: SampleAttachment[] = [
  {
    id: 'invoice-pdf',
    name: 'Счёт № 042.pdf',
    size: 81639,
    mime: 'application/pdf',
    url: '/design-fixtures/sample-invoice.pdf',
    state: 'ready',
    area: 'card',
  },
  {
    id: 'invoice-png',
    name: 'Счёт № 042.png',
    size: 34887,
    mime: 'image/png',
    url: '/design-fixtures/sample-invoice.png',
    state: 'ready',
    area: 'card',
  },
];
export const DUTY_ROWS = [
  { id: 'anna', title: 'Анна Смирнова', detail: 'Основное дежурство' },
  { id: 'mikhail', title: 'Михаил Орлов', detail: 'Подмена и поддержка' },
  { id: 'elena', title: 'Елена Петрова', detail: 'Инфраструктура' },
  { id: 'team', title: 'Общая смена', detail: 'Команда сервиса' },
];
export const SHIFTS: TimeShift[] = Array.from({ length: 190 }, (_, i) => {
  const start = new Date(Date.UTC(2026, 9, 19 + i * 2, 6)).toISOString();
  const end = new Date(Date.UTC(2026, 9, 19 + i * 2, 18)).toISOString();
  return {
    id: 'shift-' + i,
    rowId: DUTY_ROWS[i % 4].id,
    title: i % 4 === 3 ? 'Смена команды' : 'Дежурство',
    start,
    end,
    tone: i % 4 === 1 ? 'warning' : 'info',
  };
});
// Вымышленные пересечения и переход через полночь: видны полосы в разных дорожках.
SHIFTS.push(
  {
    id: 'shift-overlap',
    rowId: 'elena',
    title: 'Подмена',
    start: '2026-10-23T10:00:00Z',
    end: '2026-10-23T16:00:00Z',
    tone: 'warning',
  },
  {
    id: 'shift-night',
    rowId: 'anna',
    title: 'Ночная смена',
    start: '2026-10-22T19:00:00Z',
    end: '2026-10-23T05:00:00Z',
    tone: 'success',
  },
);
export const resolveOperations: PathResolver = (path) => {
  const card = CARDS.find((c) => path === HOME + '/item/' + c.id);
  if (card)
    return {
      type: 'object',
      serviceId: 'operations',
      title: '№ ' + card.id + ' · ' + card.title,
      icon: FileText,
      homeView: HOME,
    };
  if (path.includes('/file/'))
    return {
      type: 'object',
      serviceId: 'operations',
      title: FILES.find((f) => path.endsWith('/' + f.id))?.name ?? 'Вложение',
      icon: FileText,
      homeView: HOME,
    };
  if (path.includes('/shift/'))
    return {
      type: 'object',
      serviceId: 'operations',
      title: 'Смена дежурства',
      icon: CalendarDays,
      homeView: HOME,
    };
  return { type: 'view', serviceId: 'operations', title: 'Рабочие процессы', icon: Kanban };
};
